import type {
  AuthUserDto,
  DiagramDto,
  MeDto,
  OrgMemberDto,
  PortalOverviewDto,
} from '../generated/contracts';
import { signOut } from '../core/AccountGate';
import {
  createDiagram,
  getMe,
  getOrgMembers,
  getPortalOverview,
  NotSignedInError,
  renameDiagram,
} from '../net/api';
import { el, replace } from './portal/dom';
import { initialsOf, plural, relativeTime } from './portal/format';
import {
  ACTIVE_WINDOW_DAYS,
  directionOf,
  newestKnownActivity,
  peopleMetrics,
  roomMetrics,
} from './portal/metrics';
import { card, cardHead, kpi, pageHead, PortalShell, type PortalShellNav } from './portal/shell';

/**
 * The product portal's landing dashboard.
 *
 * ================================================================================
 * ONE SHELL, TWO VIEWS, AND THE DIFFERENCE IS DECIDED ON THE SERVER.
 * ================================================================================
 *
 * The owner's brief, verbatim: *"a handy dashboard for seeing your actual users if you're an org
 * admin, or if you're a non admin you need to see just your own scoped info."*
 *
 * So there are two views. What is NOT here is a single view with owner sections hidden by an
 * `if`: `GET /api/portal/overview` never computes another person's activity into a member's
 * response, and `GET /api/org/members` refuses a member outright with a 403. This class asks for
 * the member list only when the server has already said the caller is an owner — not to protect
 * anything (the server does that) but because a page that fires a request it knows will be
 * refused teaches the next reader that the refusal is routine.
 *
 * **The vertical rhythm is fixed: scan → compare → drill** (plan/portal-design.md). A row of
 * single numbers, then things placed against each other, then the table you actually work in.
 * That order *is* the information architecture rather than a layout preference.
 *
 * **Every number carries context, and no number is invented.** `Room` has no `CreatedAt` and no
 * owner column; `RoomGrant` does not exist; `RoomActivity` is upserted so its timestamp has no
 * history. The scan tier's room tiles stay tiles for exactly that reason — see `metrics.ts` for
 * the full audit of what is and is not derivable — but the drill tier below them is a diagrams
 * list, not a rooms one (plan/room-to-diagram.md, task R1): the diagram is what is owned and
 * shared (SPEC-132), and nobody creates, names or picks a room here any more (SPEC-153). "Enter
 * VR" is an action on a diagram row; the room it resolves to is an implementation detail this
 * page never shows.
 *
 * Each section still fetches and fails independently, as the page this replaces did: a slow
 * member list must not stop the diagrams table rendering. The overview is the one exception,
 * because both the tiles and the table are derived from it — a page that rendered with it
 * missing would show numbers that are wrong rather than absent, which is the worse failure.
 */
// spec: SPEC-132
// spec: SPEC-153
export interface PortalHomeDeps {
  getMe: () => Promise<MeDto>;
  getPortalOverview: () => Promise<PortalOverviewDto>;
  getOrgMembers: () => Promise<OrgMemberDto[]>;
  createDiagram: (name: string) => Promise<DiagramDto>;
  renameDiagram: (diagramId: string, name: string) => Promise<DiagramDto>;
}

const defaultDeps: PortalHomeDeps = {
  getMe,
  getPortalOverview,
  getOrgMembers,
  createDiagram,
  renameDiagram,
};

export class PortalHome {
  private readonly shell: PortalShell;
  private readonly deps: PortalHomeDeps;
  private readonly now: Date;

  /** The last overview fetched, so creating or renaming a diagram can re-render without a
   *  second read. */
  private overview: PortalOverviewDto | null = null;

  /** The diagram whose row is currently showing the rename form, if any — at most one at a
   *  time, the same shape `PortalPeople`'s own inline rename uses. */
  private renamingDiagramId: string | null = null;

  constructor(
    host: HTMLElement,
    account: AuthUserDto,
    /**
     * Hands a diagram to the VR host — the "Open in 3D" action, and the only way into a session
     * from this page.
     *
     * **It takes a diagram id, not a room id, and this page no longer resolves one.** It used to:
     * "Enter VR" called `POST /api/diagrams/{id}/room` here and passed the room that came back
     * to a router navigation. Both halves moved. The resolution moved because the thing that
     * needs a room is the thing that is going to stand in it, and that is another origin now
     * (`vrMain.ts` makes the identical call). The navigation moved because there is no room
     * route on this host to navigate to any more.
     *
     * What is left here is a link, which is what it should always have been: this page knows
     * which diagram was clicked and nothing else about what happens next.
     */
    private readonly onOpenInVr: (diagramId: string) => void,
    deps: Partial<PortalHomeDeps> = {},
    onOpenExports: (() => void) | null = null,
    onOpenControls: (() => void) | null = null,
    /** The org owner's People page. Absent for a member — the rail never offers it, and the
     *  server refuses every route it calls. */
    onOpenPeople: (() => void) | null = null,
    /** Injected so a test can pin the clock every relative time on this page is measured
     *  against. A page whose tiles and table disagreed across a boundary would be showing two
     *  different "todays". */
    now: Date = new Date(),
  ) {
    this.deps = { ...defaultDeps, ...deps };
    this.now = now;

    const nav: PortalShellNav = {
      go: (route) => {
        if (route === '/portal/exports') {
          onOpenExports?.();
        } else if (route === '/portal/controls') {
          onOpenControls?.();
        } else if (route === '/portal/people') {
          onOpenPeople?.();
        }
      },
      // No "Open in 3D" until the diagram list has arrived: there is nothing to open yet, and a
      // button that needs a second click once the data lands is worse than one that appears.
      enterRoom: null,
    };

    this.shell = new PortalShell(host, account, '/portal', nav);

    replace(
      this.shell.main,
      pageHead('Overview', 'Loading…'),
    );

    void this.load();
  }

  dispose(): void {
    this.shell.dispose();
  }

  private async load(): Promise<void> {
    let overview: PortalOverviewDto;

    try {
      overview = await this.deps.getPortalOverview();
    } catch (error) {
      this.renderFailure(error, "Couldn't load your org's rooms. Try reloading the page.");
      return;
    }

    this.overview = overview;
    this.shell.setOrgName(overview.orgName);
    this.shell.setIsOwner(overview.isOwner);
    this.render();

    // Owner only, and asked for only after the server has said so. A member never sends this
    // request, and would be refused if they did.
    if (overview.isOwner) {
      try {
        const members = await this.deps.getOrgMembers();
        this.renderPeople(members);
      } catch (error) {
        this.renderPeopleFailure(error);
      }
    }

    // "You" is a small read and the page is useful without it, so it lands last and inline.
    try {
      const me = await this.deps.getMe();
      this.renderMe(me);
    } catch (error) {
      this.renderMeFailure(error);
    }
  }

  // ---------------------------------------------------------------- rendering

  private render(): void {
    const overview = this.overview;

    if (overview === null) {
      return;
    }

    const rooms = roomMetrics(overview, this.now);

    // The server already orders `Diagrams` newest-changed first (DashboardEndpoints), so the
    // first entry is what a rail shortcut should offer — the diagram most likely to be the one
    // somebody just came here to open.
    const firstDiagram = overview.diagrams[0] ?? null;

    replace(
      this.shell.main,
      pageHead(overview.isOwner ? 'Overview' : 'Your workspace'),

      this.scanRow(overview, rooms),
      this.diagramsCard(overview),

      // The People card is an owner's, and it is not rendered as an empty placeholder for a
      // member — an empty card would say "you have no colleagues", which is a different and
      // false statement.
      overview.isOwner ? el('div', { 'data-people-slot': 'true' }, [this.peoplePlaceholder()]) : null,

      el('div', { 'data-me-slot': 'true' }),
    );

    // The diagram list has arrived, so entering VR is now a thing that can be done. Re-created
    // rather than toggled, because the shell rebuilds its nav in one pass.
    if (firstDiagram !== null) {
      this.enableEnterVr(firstDiagram.id);
    }
  }

  /**
   * The rail's one-click "Open in 3D" shortcut, pointed at the diagram most recently changed.
   * Unlike a diagram row's own button (below), this fires from the rail before that
   * diagram's own row necessarily exists on screen. It hands over the same thing either way —
   * the diagram id, and nothing else (see {@link onOpenInVr}).
   */
  private enableEnterVr(diagramId: string): void {
    const existing = this.shell.root.querySelector('[data-enter-room]');

    if (existing !== null) {
      return;
    }

    const button = el('button', {
      type: 'button',
      class: 'btn btn-primary',
      'data-enter-room': 'true',
      text: 'Open in 3D',
    });

    button.addEventListener('click', () => this.openInVr(diagramId));

    this.shell.root
      .querySelector('.rail-nav')
      ?.append(el('div', { class: 'rail-enter' }, [button]));
  }

  /**
   * Hands a diagram to the VR host — where both the rail shortcut and a diagram row's own
   * "Open in 3D" button end up.
   *
   * **Synchronous now, and with no failure path of its own.** It used to make a request, so it
   * needed a disabled button, a `NotSignedInError` branch and somewhere to put an error message.
   * None of that has anything left to describe: the request it made is made on the other side
   * now, by the host that actually needs a room, and a page navigation either happens or the
   * browser says so. A signed-out visitor is handled where it can be handled properly — the VR
   * host's own sign-in gate — rather than by this page guessing from a rejected fetch.
   */
  private openInVr(diagramId: string): void {
    this.onOpenInVr(diagramId);
  }

  /**
   * The scan tier. Four tiles for an owner, four for a member, and **every context slot is one
   * of the four kinds plan/portal-design.md allows** — which is not a coincidence, it is what
   * the data supports:
   *
   *   Rooms       → a DENOMINATOR ("n of N active this week"), plus a team BREAKDOWN.
   *   Here now    → a BREAKDOWN, live from RoomPresenceRegistry and stored nowhere.
   *   People      → a DENOMINATOR (owner) / your own RECENCY (member).
   *   Exports     → a real 30-over-30 DELTA, the only one on this surface.
   */
  private scanRow(overview: PortalOverviewDto, rooms: ReturnType<typeof roomMetrics>): HTMLElement {
    const exports = overview.exports;
    const direction = directionOf(exports.recentCount, exports.priorCount);
    const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■';

    const tiles: HTMLElement[] = [
      kpi({
        label: 'Rooms',
        value: String(rooms.rooms),
        // The same count means two different things by role, and the wording has to say which:
        // the server sends an owner every room's activity and a member only their own, so
        // "3 of 4 active this week" would be a false org-wide claim on a member's page. Caught
        // in the rendered PNG, not by an assertion.
        context:
          rooms.rooms === 0
            ? 'none yet'
            : overview.isOwner
              ? `${rooms.roomsActiveRecently} of ${rooms.rooms} active in ${ACTIVE_WINDOW_DAYS} days`
              : `you were in ${rooms.roomsActiveRecently} of ${rooms.rooms} in ${ACTIVE_WINDOW_DAYS} days`,
        note: rooms.teams === 0 ? undefined : `Across ${plural(rooms.teams, 'team')}`,
      }),

      kpi({
        label: 'Here now',
        value: String(rooms.hereNow),
        context:
          rooms.hereNow === 0
            ? 'nobody is in a room'
            : `in ${rooms.roomsOccupied} of ${plural(rooms.rooms, 'room')}`,
      }),

      overview.isOwner
        ? el('section', { class: 'card kpi', 'data-people-tile': 'true' }, [
            el('div', { class: 'kpi-label', text: 'People' }),
            el('div', { class: 'kpi-value', text: '—' }),
            el('div', { class: 'kpi-context', text: 'loading…' }),
          ])
        : kpi({
            label: 'Your activity',
            value: relativeTime(rooms.yourLastActiveAt, this.now),
            context:
              rooms.yourLastRoomName === null
                ? 'you have not worked in a room yet'
                : `in ${rooms.yourLastRoomName}`,
            note: `Active in ${rooms.roomsYouHaveUsed} of ${plural(rooms.rooms, 'room')}`,
          }),

      kpi({
        label: 'Exports',
        value: String(exports.total),
        context:
          exports.recentCount === 0 && exports.priorCount === 0
            ? `${arrow} none in ${exports.windowDays} days`
            : `${arrow} ${plural(exports.recentCount, 'export')} in the last ${exports.windowDays} days`,
        direction,
        note: `${exports.priorCount} in the ${exports.windowDays} days before that`,
      }),

      this.usageTile(overview),
    ];

    return el('div', { class: 'kpi-row section' }, tiles);
  }

  /**
   * User-minutes — plan/usage-analytics.md's Decision D, and the fifth scan tile. `SUM(Participants)`
   * over the window, never `Spectators`, with the same 30-over-30 delta shape `Exports` already
   * carries, plus a breakdown (rooms active) as a second kind of context on the same tile.
   *
   * A room-minute rate is deliberately not a second tile here: `Room` has no capacity column, so
   * "fill rate" can only mean occupied minutes over elapsed minutes, and stating that definition
   * in a caption is exactly the schema-explaining prose plan/portal-design.md's second rule
   * removed everywhere else. It stays a plain, labelled number rather than a percentage nobody
   * asked to have explained.
   */
  private usageTile(overview: PortalOverviewDto): HTMLElement {
    const usage = overview.usage;
    const direction = directionOf(usage.userMinutes, usage.priorUserMinutes);
    const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '■';

    return kpi({
      label: 'User-minutes',
      value: String(usage.userMinutes),
      context:
        usage.userMinutes === 0 && usage.priorUserMinutes === 0
          ? `${arrow} none in ${usage.windowDays} days`
          : `${arrow} vs ${usage.priorUserMinutes} in the ${usage.windowDays} days before that`,
      direction,
      note: usage.roomsActive === 0 ? undefined : `Across ${plural(usage.roomsActive, 'room')}`,
    });
  }

  /**
   * The drill tier, and the centre of this page.
   *
   * **A list of diagrams, not rooms** (plan/room-to-diagram.md, task R1): the diagram is what is
   * owned and shared (SPEC-132), and rooms have been taken off this page entirely — nobody
   * creates, names or picks one here any more (SPEC-153). Two actions per row: View, into the
   * existing desktop viewer, and Enter VR, which resolves the interim room behind the scenes.
   */
  private diagramsCard(overview: PortalOverviewDto): HTMLElement {
    const diagrams = overview.diagrams;

    const head = el('thead', {}, [
      el('tr', {}, [this.column('Diagram'), this.column('Last changed'), this.column('')]),
    ]);

    const body =
      diagrams.length === 0
        ? el('tbody', {}, [
            el('tr', {}, [el('td', { colspan: '3', class: 'empty', text: 'No diagrams yet. Create one below.' })]),
          ])
        : el('tbody', {}, diagrams.map((diagram) => this.diagramRow(diagram)));

    return card([
      cardHead('Diagrams', plural(diagrams.length, 'diagram')),
      // Three columns rarely overflows, but `.table-scroll` costs nothing to keep for the same
      // phone-viewport reason the room table used it. `data-diagrams-table` exists so a test
      // (and nothing else) can tell this table apart from the People card's own `tbody` —
      // there is otherwise nothing distinguishing two plain `<table>` elements on the page.
      el('div', { class: 'table-scroll' }, [el('table', { 'data-diagrams-table': 'true' }, [head, body])]),
      this.createDiagramForm(),
    ]);
  }

  private diagramRow(diagram: DiagramDto): HTMLElement {
    const renaming = this.renamingDiagramId === diagram.id;

    const rename = el('button', { type: 'button', class: 'btn', 'data-rename': diagram.id, text: 'Rename' });
    rename.addEventListener('click', () => {
      this.renamingDiagramId = diagram.id;
      this.render();
    });

    // "View" is the way into the desktop viewer (plan/diagram-extraction.md, A5) and it is a
    // real `<a href>` rather than a button: a diagram link is the one URL in this product
    // somebody genuinely sends to another person, so it has to be copyable, openable in a new
    // tab and reachable by middle-click.
    const view = el('a', {
      class: 'btn',
      href: `/diagram?diagramId=${encodeURIComponent(diagram.id)}`,
      'data-view-diagram': diagram.id,
      text: 'View',
    });

    const enter = el('button', {
      type: 'button',
      class: 'btn btn-primary',
      'data-enter-diagram': diagram.id,
      text: 'Open in 3D',
    });
    enter.addEventListener('click', () => this.openInVr(diagram.id));

    return el('tr', {}, [
      el('td', {}, [renaming ? this.renameForm(diagram) : el('strong', { text: diagram.name })]),
      el('td', { class: 'muted', text: relativeTime(diagram.updatedAt, this.now) }),
      el('td', {}, [el('div', { class: 'row' }, [renaming ? null : view, renaming ? null : rename, renaming ? null : enter])]),
    ]);
  }

  private renameForm(diagram: DiagramDto): HTMLElement {
    const input = el('input', {
      type: 'text',
      class: 'field',
      'data-rename-input': diagram.id,
      value: diagram.name,
      maxlength: '200',
      'aria-label': `Name for ${diagram.name}`,
    });

    const save = el('button', { type: 'submit', class: 'btn btn-primary', 'data-rename-save': diagram.id, text: 'Save' });
    const cancel = el('button', { type: 'button', class: 'btn', 'data-rename-cancel': diagram.id, text: 'Cancel' });

    cancel.addEventListener('click', () => {
      this.renamingDiagramId = null;
      this.render();
    });

    const form = el('form', { class: 'row', 'data-rename-form': diagram.id }, [input, save, cancel]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitRenameDiagram(diagram, input, save);
    });

    return form;
  }

  private async submitRenameDiagram(diagram: DiagramDto, input: HTMLInputElement, submit: HTMLButtonElement): Promise<void> {
    const name = input.value.trim();

    if (name.length === 0) {
      return;
    }

    submit.disabled = true;

    try {
      const updated = await this.deps.renameDiagram(diagram.id, name);

      if (this.overview !== null) {
        this.overview = {
          ...this.overview,
          diagrams: this.overview.diagrams.map((d) => (d.id === updated.id ? updated : d)),
        };
      }

      this.renamingDiagramId = null;
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      submit.disabled = false;
      submit.after(el('span', { class: 'muted', text: describe(error, "Couldn't save that name.") }));
    }
  }

  private column(label: string, numeric = false): HTMLElement {
    // A plain label, not a button that does nothing. Sorting a handful of diagrams that are
    // already ordered by "changed most recently" would be chrome.
    return el('th', { scope: 'col', class: numeric ? 'num' : null }, [
      el('span', { class: 'col', text: label }),
    ]);
  }

  private createDiagramForm(): HTMLElement {
    const input = el('input', {
      type: 'text',
      class: 'field',
      'data-create-diagram-name': 'true',
      placeholder: 'New diagram name',
      maxlength: '200',
      'aria-label': 'New diagram name',
    });

    const submit = el('button', {
      type: 'submit',
      class: 'btn',
      'data-create-diagram-submit': 'true',
      text: 'Create diagram',
    });

    const form = el('form', { class: 'table-tools', 'data-create-diagram-form': 'true' }, [
      el('div', { class: 'row' }, [input, submit]),
    ]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitCreateDiagram(input, submit);
    });

    return form;
  }

  private async submitCreateDiagram(input: HTMLInputElement, submit: HTMLButtonElement): Promise<void> {
    const name = input.value.trim();

    if (name.length === 0) {
      return;
    }

    submit.disabled = true;

    try {
      await this.deps.createDiagram(name);
      input.value = '';

      // Re-read rather than splicing the response in by hand, so the table always shows exactly
      // what the server has.
      this.overview = await this.deps.getPortalOverview();
      this.render();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      submit.disabled = false;
      submit.after(el('span', { class: 'muted', text: describe(error, "Couldn't create the diagram.") }));
      return;
    }

    submit.disabled = false;
  }

  private peoplePlaceholder(): HTMLElement {
    return card([cardHead('People'), el('div', { class: 'empty', text: 'Loading…' })]);
  }

  /**
   * The owner's people table. Names, roles and a recency — no avatars, because `AppUser` has no
   * photo column and nothing in this product stores an image of a person. Initials on a token
   * colour is what principle 9 actually asks for.
   */
  private renderPeople(members: readonly OrgMemberDto[]): void {
    const slot = this.shell.root.querySelector('[data-people-slot]');

    if (slot === null) {
      return;
    }

    const people = peopleMetrics(members, this.now);
    const tile = this.shell.root.querySelector('[data-people-tile]');

    if (tile !== null) {
      replace(
        tile,
        el('div', { class: 'kpi-label', text: 'People' }),
        el('div', { class: 'kpi-value', text: String(people.people) }),
        el('div', {
          class: 'kpi-context',
          text: `${people.activeRecently} of ${people.people} seen in ${ACTIVE_WINDOW_DAYS} days`,
        }),
        people.neverSeen === 0
          ? null
          : el('div', {
              class: 'kpi-note',
              text: `${plural(people.neverSeen, 'person', 'people')} never seen`,
            }),
      );
    }

    const rows =
      members.length === 0
        ? el('tbody', {}, [el('tr', {}, [el('td', { colspan: '3', class: 'empty', text: 'Nobody else here yet.' })])])
        : el(
            'tbody',
            {},
            [...members].map((member) =>
              el('tr', {}, [
                el('td', {}, [
                  el('div', { class: 'person' }, [
                    el('span', {
                      class: 'initials',
                      'aria-hidden': 'true',
                      text: initialsOf(member.displayName, member.email),
                    }),
                    el('span', { class: 'person-name' }, [
                      el('strong', { text: member.displayName || member.email }),
                      el('span', { text: member.email }),
                    ]),
                  ]),
                ]),
                el('td', {}, [
                  el('span', {
                    class: 'pill',
                    'data-status': member.role === 'owner' ? 'ok' : null,
                    text: member.role,
                  }),
                ]),
                el('td', { class: 'muted', text: relativeTime(member.lastSeenAt ?? null, this.now) }),
              ]),
            ),
          );

    replace(
      slot,
      card([
        cardHead('People', `${plural(members.length, 'person', 'people')} in your org`),
        el('div', { class: 'table-scroll' }, [
          el('table', {}, [
            el('thead', {}, [
              el('tr', {}, [this.column('Person'), this.column('Role'), this.column('Last seen')]),
            ]),
            rows,
          ]),
        ]),
      ]),
    );
  }

  private renderPeopleFailure(error: unknown): void {
    if (error instanceof NotSignedInError) {
      void signOut();
      return;
    }

    const slot = this.shell.root.querySelector('[data-people-slot]');

    if (slot !== null) {
      replace(
        slot,
        card([
          cardHead('People'),
          el('div', { class: 'empty', text: describe(error, "Couldn't load the people in your org.") }),
        ]),
      );
    }
  }

  private renderMe(me: MeDto): void {
    const slot = this.shell.root.querySelector('[data-me-slot]');

    if (slot === null) {
      return;
    }

    replace(
      slot,
      card([
        cardHead('You'),
        el('table', {}, [
          el('tbody', {}, [
            row('Name', me.displayName),
            row('Email', me.email),
            row('Org', me.orgName),
            row('Role', me.role),
          ]),
        ]),
      ]),
    );
  }

  private renderMeFailure(error: unknown): void {
    if (error instanceof NotSignedInError) {
      void signOut();
      return;
    }

    const slot = this.shell.root.querySelector('[data-me-slot]');

    if (slot !== null) {
      replace(slot, el('div', { class: 'banner' }, [el('span', { text: describe(error, "Couldn't load your account.") })]));
    }
  }

  private renderFailure(error: unknown, fallback: string): void {
    // A session that expired while the portal was open routes back to sign-in rather than
    // showing "couldn't load" forever — `signOut()` is the same full reload either way, and it
    // lands on the router's guard, which finds no session and shows the sign-in screen.
    if (error instanceof NotSignedInError) {
      void signOut();
      return;
    }

    replace(
      this.shell.main,
      pageHead('Overview'),
      el('div', { class: 'banner' }, [
        el('strong', { text: 'Your dashboard did not load. ' }),
        el('span', { text: describe(error, fallback) }),
      ]),
    );
  }

}

function row(label: string, value: string): HTMLElement {
  return el('tr', {}, [el('td', { class: 'muted', text: label }), el('td', { text: value })]);
}

function describe(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}

export { newestKnownActivity };
